/**
 * Vulnerabilities Table — per-scan Report page.
 *
 * Lists every vulnerability discovered in a scan: CVE id (clickable),
 * affected component + version, severity, source (NVD / OSV / GHSA),
 * CVSS + EPSS scores, and the timestamp the row was attributed.
 *
 * Renders through the canonical Athena chrome:
 *   - DataTable (sortable headers + bordered card via `dtStyles.shell`)
 *   - TableToolbar (filter funnel button + count chip; no search)
 *   - TablePagination (functional page-size dropdown)
 *   - FilterDrawer (side-panel multi-select for Component + Severity —
 *     replaces the bespoke column-popover funnel buttons used in the
 *     hand-rolled predecessor)
 *
 * The consumer owns filter + sort + pagination state internally; Report.jsx
 * only passes the full `data` array + an `onRowClick` callback.
 *
 * Severity column sort uses the priority map (CRITICAL > HIGH > MEDIUM >
 * LOW > UNASSIGNED), not alphabetical. Severity pill colours encode the
 * public severity palette via `getSeverityColor`.
 */
import React, { useMemo, useState, memo } from 'react';
import DataTable from '../../components/ui/DataTable/DataTable';
import TableToolbar from '../../components/ui/DataTable/TableToolbar';
import TablePagination from '../../components/ui/DataTable/TablePagination';
import FilterDrawer from '../../components/ui/DataTable/FilterDrawer';
import dtStyles from '../../components/ui/DataTable/DataTable.module.css';
import LevelBadge from '../../components/ui/LevelBadge';
import CategoryPill from '../../components/ui/CategoryPill/CategoryPill';
import { getSeverityColor } from '../../utils/severityStyle';

const DEFAULT_PAGE_SIZE = 20;
const SEVERITY_LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNASSIGNED'];
const SEVERITY_ORDER = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1, UNASSIGNED: 0 };

const formatEpss = (epss) => {
  if (epss === null || epss === undefined) return 'N/A';
  const pct = (epss * 100).toFixed(2);
  return `${pct}%`;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'N/A';
  }
};

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1Z"
      fill="currentColor"
      opacity="0.5"
    />
  </svg>
);

const VulnerabilitiesTable = memo(({
  data = [],
  loading = false,
  onRowClick,
}) => {
  const [sort, setSort] = useState({ column: 'cvssScore', direction: 'desc' });
  const [componentFilters, setComponentFilters] = useState([]);
  const [severityFilters, setSeverityFilters] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const uniqueComponents = useMemo(
    () => Array.from(new Set(data.map((v) => v.component).filter(Boolean))).sort(),
    [data],
  );

  // Decorate every row with a stable composite key so DataTable's rowKey
  // resolves uniquely even when the same vulnerability appears against
  // multiple components in the same scan.
  const decoratedData = useMemo(
    () =>
      data.map((row, idx) => ({
        ...row,
        _rowKey: `${row.vulnerabilityId ?? 'unknown'}-${row.component ?? '?'}-${idx}`,
      })),
    [data],
  );

  // Filter (component + severity) → sort (special severity priority
  // when sorting by severity) → paginate (slice the page from the
  // full sorted set).
  const filteredData = useMemo(() => {
    return decoratedData.filter((v) => {
      if (componentFilters.length > 0 && !componentFilters.includes(v.component)) return false;
      if (severityFilters.length > 0) {
        const s = v.severity?.toUpperCase();
        if (!severityFilters.includes(s)) return false;
      }
      return true;
    });
  }, [decoratedData, componentFilters, severityFilters]);

  const sortedData = useMemo(() => {
    if (!sort?.column) return filteredData;
    const dir = sort.direction === 'desc' ? -1 : 1;
    return [...filteredData].sort((a, b) => {
      if (sort.column === 'severity') {
        const av = SEVERITY_ORDER[a.severity?.toUpperCase()] ?? -1;
        const bv = SEVERITY_ORDER[b.severity?.toUpperCase()] ?? -1;
        return (av - bv) * dir;
      }
      const av = a[sort.column];
      const bv = b[sort.column];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') {
        return av.toLowerCase().localeCompare((bv ?? '').toString().toLowerCase()) * dir;
      }
      return ((av ?? 0) - (bv ?? 0)) * dir;
    });
  }, [filteredData, sort]);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const handleFilterChange = (setter) => (next) => {
    setter(next);
    setCurrentPage(1); // Filter change → window starts fresh.
  };

  const handlePageSizeChange = (next) => {
    setPageSize(next);
    setCurrentPage(1);
  };

  const filters = useMemo(
    () => [
      {
        key: 'component',
        label: 'Component',
        values: componentFilters,
        onChange: handleFilterChange(setComponentFilters),
        options: uniqueComponents.map((c) => ({
          value: c,
          label: c,
          count: data.filter((v) => v.component === c).length,
        })),
      },
      {
        key: 'severity',
        label: 'Severity',
        values: severityFilters,
        onChange: handleFilterChange(setSeverityFilters),
        options: SEVERITY_LEVELS.map((s) => ({
          value: s,
          label: s,
          count: data.filter((v) => v.severity?.toUpperCase() === s).length,
        })),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [componentFilters, severityFilters, uniqueComponents, data],
  );

  const hasActiveFilters = componentFilters.length > 0 || severityFilters.length > 0;

  const columns = useMemo(
    () => [
      {
        key: 'vulnerabilityId',
        label: 'Vulnerability',
        sortable: true,
        width: '20%',
        // Plain text rendering — `--text-primary` flows via the row
        // cell's inherited colour, so no inline style needed.
        render: (row) => row.vulnerabilityId || 'N/A',
      },
      {
        key: 'component',
        label: 'Component',
        sortable: true,
        width: '20%',
        render: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontWeight: 500 }}>{row.component || 'N/A'}</span>
            {row.componentVersion && (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                v{row.componentVersion}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'severity',
        label: 'Severity',
        sortable: true,
        width: '12%',
        align: 'center',
        // Canonical TARA workflow severity rendering — LevelBadge
        // filled-background swatch with the colour resolved from
        // severityStyle's `getSeverityColor()`. Same primitive Step 6
        // Risk Assessment uses for risk-level cells.
        render: (row) => (
          <LevelBadge
            color={getSeverityColor(row.severity)}
            label={row.severity || 'N/A'}
            size="sm"
          />
        ),
      },
      {
        key: 'source',
        label: 'Source',
        sortable: true,
        width: '10%',
        align: 'center',
        // CategoryPill default neutral tone (grey-ish outlined pill).
        render: (row) => <CategoryPill label={row.source || 'N/A'} size="sm" />,
      },
      {
        key: 'cvssScore',
        label: 'CVSS',
        sortable: true,
        width: '10%',
        // Plain numeric text — no colour ramp on CVSS.
        render: (row) =>
          row.cvssScore != null ? row.cvssScore.toFixed(1) : 'N/A',
      },
      {
        key: 'epssScore',
        label: 'EPSS',
        sortable: true,
        width: '10%',
        render: (row) => formatEpss(row.epssScore),
      },
      {
        key: 'attributedOn',
        label: 'Attributed on',
        sortable: true,
        width: '18%',
        render: (row) => (
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {formatDate(row.attributedOn)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className={`${dtStyles.shell} ${dtStyles.shellPlain}`}>
      <FilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
      />
      <DataTable
        data={pageRows}
        columns={columns}
        rowKey="_rowKey"
        loading={loading}
        sort={sort}
        onSortChange={(column, direction) =>
          setSort(column ? { column, direction } : null)
        }
        onRowClick={(row) => onRowClick?.(row.source, row.vulnerabilityId)}
        emptyState={{
          icon: <EmptyIcon />,
          title: 'No vulnerabilities found',
          description:
            hasActiveFilters
              ? 'No vulnerabilities match the selected filters'
              : 'This analysis does not contain any vulnerabilities',
        }}
        toolbar={
          <TableToolbar
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || hasActiveFilters}
            count={{ value: sortedData.length, label: 'vulnerabilities' }}
          />
        }
        pagination={
          <TablePagination
            currentPage={currentPage}
            totalItems={sortedData.length}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handlePageSizeChange}
          />
        }
      />
    </div>
  );
});

VulnerabilitiesTable.displayName = 'VulnerabilitiesTable';

export default VulnerabilitiesTable;
